import type { TournamentCurrentResponse } from '@cradle7/shared';
import { useSyncExternalStore } from 'react';
import { api } from './api';

/**
 * Phase 2k — Tournament Weekend store. Plain-module + emit pattern mirroring
 * `challengeStore`: bootAuth hydrates once, HomeSection reads via
 * useSyncExternalStore. Failure is non-fatal — the UI falls back to the
 * regular Daily Challenge card when the tournament cache is null or inactive.
 */

let cache: TournamentCurrentResponse | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((fn) => fn());
}

export function getTournament(): TournamentCurrentResponse | null {
  return cache;
}

export function setTournament(t: TournamentCurrentResponse | null): void {
  cache = t;
  emit();
}

export function subscribeTournament(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Self-fetch helper used by `bootAuth`. Swallows errors so the boot path
 * doesn't depend on tournament reachability — null cache leaves the regular
 * daily-challenge card visible.
 */
export async function hydrateTournament(): Promise<void> {
  try {
    const resp = await api.getTournament();
    cache = resp;
    emit();
  } catch (err) {
    console.warn('[tournamentStore] hydrate failed', err);
  }
}

/** Test hook: clear in-memory state + listeners. */
export function _resetTournamentStore(): void {
  cache = null;
  listeners.clear();
}

let snapshotKey: TournamentCurrentResponse | null = null;
let snapshotCache: TournamentCurrentResponse | null = null;

function getSnapshot(): TournamentCurrentResponse | null {
  if (snapshotKey === cache) return snapshotCache;
  snapshotKey = cache;
  snapshotCache = cache;
  return cache;
}

export function useTournament(): TournamentCurrentResponse | null {
  return useSyncExternalStore(subscribeTournament, getSnapshot, getSnapshot);
}
